/** Normalize #RGB hex to lowercase #rrggbb, or null if invalid. */
export function normalizeHex(hex) {
  if (!hex || typeof hex !== "string") return null;
  const s = hex.trim();
  const m = /^#?([a-fA-F0-9]{6})$/.exec(s);
  if (!m) return null;
  return `#${m[1].toLowerCase()}`;
}

/** Rec. 709 relative luminance (0–1) for a hex color. */
export function relativeLuminance(hex) {
  const n = normalizeHex(hex);
  if (!n) return 0.5;
  const r = parseInt(n.slice(1, 3), 16) / 255;
  const g = parseInt(n.slice(3, 5), 16) / 255;
  const b = parseInt(n.slice(5, 7), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Ink color for text on a bead/swatch: black on light backgrounds, white on dark.
 */
export function contrastInkForHex(hex) {
  return relativeLuminance(hex) > 0.55 ? "#000000" : "#ffffff";
}

/** True when R≈G≈B (grayscale / white / black beads). */
export function isGrayscaleHex(hex) {
  const n = normalizeHex(hex);
  if (!n) return false;
  const r = parseInt(n.slice(1, 3), 16);
  const g = parseInt(n.slice(3, 5), 16);
  const b = parseInt(n.slice(5, 7), 16);
  return r === g && g === b;
}

/** Near-white grayscale (needs black letter ink to stay readable). */
export function isNearWhiteHex(hex) {
  const n = normalizeHex(hex);
  if (!n) return false;
  const r = parseInt(n.slice(1, 3), 16);
  const g = parseInt(n.slice(3, 5), 16);
  const b = parseInt(n.slice(5, 7), 16);
  // Literally white or very close (e.g. #f5f5f5–#ffffff).
  return r >= 240 && g >= 240 && b >= 240;
}

/**
 * Letter ink: white on chromatic beads and most grayscale beads;
 * black only when the bead is white / very near white.
 */
export function letterInkForHex(hex) {
  if (!isGrayscaleHex(hex)) return "#ffffff";
  return isNearWhiteHex(hex) ? "#000000" : "#ffffff";
}

export const HARMONY_MODE_OPTIONS = [
  {
    id: "analogous",
    label: "Analogous",
    hint: "Neighboring hues on the wheel — low contrast, cohesive.",
  },
  {
    id: "complementary",
    label: "Complementary",
    hint: "Opposite colors (~180° apart) — high contrast, e.g. blue and orange.",
  },
  {
    id: "triadic",
    label: "Triadic",
    hint: "Three evenly spaced hues (120°) — balanced and vibrant.",
  },
  {
    id: "split",
    label: "Split-complementary",
    hint: "Base hue plus two hues near its complement — strong contrast with a bit less tension than pure complementary.",
  },
];

export const DEFAULT_HARMONY_MODE = "analogous";

export function normalizeHarmonyMode(mode) {
  const allowed = new Set(HARMONY_MODE_OPTIONS.map((o) => o.id));
  const s = typeof mode === "string" ? mode : "";
  return allowed.has(s) ? s : DEFAULT_HARMONY_MODE;
}

/**
 * @typedef {{
 *   harmonyMode?: string,
 *   paletteStructure?: number,
 *   temperature?: number,
 *   vibrancy?: number,
 *   spread?: number,
 *   lightnessBias?: number,
 *   chroma?: number,
 *   preserveContrast?: number,
 *   baseColor?: string | null,
 * }} PaletteDials
 */
export const DEFAULT_PALETTE_DIALS = Object.freeze({
  harmonyMode: DEFAULT_HARMONY_MODE,
  paletteStructure: 0.2,
  temperature: 0,
  vibrancy: 0,
  spread: 0,
  lightnessBias: 0,
  chroma: 0,
  preserveContrast: 0,
});

/**
 * @param {Partial<PaletteDials> | null | undefined} dials
 * @returns {Required<PaletteDials>}
 */
export function normalizePaletteDials(dials) {
  const d = dials && typeof dials === "object" ? dials : {};
  const clamp = (x) => {
    const n = Number(x);
    if (!Number.isFinite(n)) return 0;
    return Math.max(-1, Math.min(1, n));
  };
  const clamp01 = (x, fallback = DEFAULT_PALETTE_DIALS.paletteStructure) => {
    const n = Number(x);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(0, Math.min(1, n));
  };
  return {
    harmonyMode: normalizeHarmonyMode(d.harmonyMode),
    paletteStructure: clamp01(d.paletteStructure),
    temperature: clamp(d.temperature),
    vibrancy: clamp(d.vibrancy),
    spread: clamp(d.spread),
    lightnessBias: clamp(d.lightnessBias),
    chroma: clamp(d.chroma),
    preserveContrast: clamp(d.preserveContrast),
    baseColor: normalizeHex(d.baseColor) || null,
  };
}

function rgbToHex(r, g, b) {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const h = x.toString(16);
        return h.length === 1 ? "0" + h : h;
      })
        .join("")
  );
}

/** @returns {[number, number, number]} H 0–360, S/L 0–100 */
function hexToHsl(hex) {
  const n = normalizeHex(hex);
  if (!n) return [0, 0, 50];
  const r = parseInt(n.slice(1, 3), 16) / 255;
  const g = parseInt(n.slice(3, 5), 16) / 255;
  const b = parseInt(n.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
  const hh = ((h % 360) + 360) % 360;
  const ss = Math.max(0, Math.min(100, s)) / 100;
  const ll = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * ll - 1)) * ss;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = ll - c / 2;

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (hh < 60) {
    r1 = c;
    g1 = x;
  } else if (hh < 120) {
    r1 = x;
    g1 = c;
  } else if (hh < 180) {
    g1 = c;
    b1 = x;
  } else if (hh < 240) {
    g1 = x;
    b1 = c;
  } else if (hh < 300) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }

  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);
  return rgbToHex(r, g, b);
}

const wrapHue = (h) => ((h % 360) + 360) % 360;

/** Shortest-path hue interpolation (degrees). */
function lerpHue(h0, h1, t) {
  const a = wrapHue(h0);
  const b = wrapHue(h1);
  let d = b - a;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return wrapHue(a + t * d);
}

/** `temperature` −1 = cooler (toward blue), +1 = warmer (toward orange). */
function shiftHueTowardWarmCool(h, temperature) {
  const t = Math.max(-1, Math.min(1, temperature));
  if (Math.abs(t) < 0.001) return wrapHue(h);
  const warm = 32;
  const cool = 218;
  const target = t > 0 ? warm : cool;
  const strength = Math.abs(t) * 0.58;
  return lerpHue(h, target, strength);
}

/** `vibrancy` −1 = subdued, +1 = bright (saturation + lightness). */
function applyVibrancySL(s, l, vibrancy) {
  const v = Math.max(-1, Math.min(1, vibrancy));
  if (Math.abs(v) < 0.001) return [s, l];
  const t = (v + 1) / 2;
  const sMul = 0.52 + 0.58 * t;
  const lAdd = -10 + t * 18;
  return [
    Math.max(6, Math.min(100, s * sMul)),
    Math.max(10, Math.min(90, l + lAdd)),
  ];
}

/** Circular mean hue (degrees). */
function meanHueDeg(hues) {
  if (!hues.length) return 0;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < hues.length; i++) {
    const rad = (wrapHue(hues[i]) * Math.PI) / 180;
    sx += Math.cos(rad);
    sy += Math.sin(rad);
  }
  return wrapHue((Math.atan2(sy, sx) * 180) / Math.PI);
}

/** `spread` below 0 pulls hues toward palette mean; above 0 pushes them apart on the wheel. */
function applyHueSpreadToPalette(hexes, spread) {
  const sp = Math.max(-1, Math.min(1, spread));
  if (Math.abs(sp) < 0.001) return hexes;
  const hsls = hexes.map((hex) => {
    const n = normalizeHex(hex);
    return n ? hexToHsl(n) : [0, 0, 50];
  });
  const Hc = meanHueDeg(hsls.map((t) => t[0]));
  const scale = Math.max(0.15, Math.min(1.9, 1 + sp * 0.72));
  return hexes.map((hex, i) => {
    const n = normalizeHex(hex);
    if (!n) return hex;
    let [h, s, l] = hsls[i];
    let dh = h - Hc;
    if (dh > 180) dh -= 360;
    if (dh < -180) dh += 360;
    h = wrapHue(Hc + dh * scale);
    return hslToHex(h, s, l);
  });
}

function lerpHexHsl(hexA, hexB, t) {
  if (t <= 0) return hexA;
  if (t >= 1) return hexB;
  const na = normalizeHex(hexA);
  const nb = normalizeHex(hexB);
  if (!na || !nb) return hexA;
  const [h1, s1, l1] = hexToHsl(na);
  const [h2, s2, l2] = hexToHsl(nb);
  const h = lerpHue(h1, h2, t);
  const s = s1 + (s2 - s1) * t;
  const l = l1 + (l2 - l1) * t;
  return hslToHex(h, s, l);
}

/**
 * Remap palette slots so dark/light order of originals matches new colors (strength 0–1).
 */
function remappedByOldLightness(orderedCanon, palette, strength) {
  const n = orderedCanon.length;
  if (n <= 1 || strength < 0.001) return palette.slice();
  const oldL = orderedCanon.map((c) => {
    const x = normalizeHex(c);
    return x ? hexToHsl(x)[2] : 50;
  });
  const idxByOldL = oldL.map((_, i) => i).sort((a, b) => oldL[a] - oldL[b]);
  const palWithL = palette.map((c, i) => {
    const x = normalizeHex(c);
    return { i, l: x ? hexToHsl(x)[2] : 50, hex: c };
  });
  palWithL.sort((a, b) => a.l - b.l);
  const out = palette.slice();
  for (let rank = 0; rank < n; rank++) {
    const origIdx = idxByOldL[rank];
    const targetHex = palWithL[rank].hex;
    out[origIdx] = lerpHexHsl(palette[origIdx], targetHex, strength);
  }
  return out;
}

/** `preserveContrast` above 0 matches old lightness ranking; below 0 flattens new colors toward mean L. */
export function applyContrastRemapToPaletteOrder(orderedCanon, palette, preserveContrast) {
  const pc = Math.max(-1, Math.min(1, preserveContrast));
  if (Math.abs(pc) < 0.001) return ensureDistinctHexes(palette.slice());
  const n = orderedCanon.length;
  const rankBlend = Math.max(0, pc);
  const flatBlend = Math.max(0, -pc);
  let out = palette.slice();
  if (rankBlend > 0.001) {
    out = remappedByOldLightness(orderedCanon, out, rankBlend);
  }
  if (flatBlend > 0.001) {
    const avgL =
      out.reduce((acc, hex) => {
        const x = normalizeHex(hex);
        return acc + (x ? hexToHsl(x)[2] : 50);
      }, 0) / n;
    out = out.map((hex) => {
      const x = normalizeHex(hex);
      if (!x) return hex;
      const [h, s, l] = hexToHsl(x);
      const l2 = l + (avgL - l) * flatBlend * 0.92;
      return hslToHex(h, s, Math.max(10, Math.min(88, l2)));
    });
  }
  // Flattening toward mean lightness (and lightness-rank blending) can converge
  // two slots onto one color, which would drop a color from the pattern.
  return ensureDistinctHexes(out);
}

function polishSingleHex(hex, d) {
  const n = normalizeHex(hex);
  if (!n) return hex;
  let [h, s, l] = hexToHsl(n);
  h = shiftHueTowardWarmCool(h, d.temperature);
  [s, l] = applyVibrancySL(s, l, d.vibrancy);
  l += d.lightnessBias * 16;
  const chromaT = (d.chroma + 1) / 2;
  s *= 0.5 + 0.5 * chromaT;
  s = Math.max(4, Math.min(100, s));
  l = Math.max(8, Math.min(92, l));
  return hslToHex(h, s, l);
}

function polishPalette(hexes, dials) {
  const d = normalizePaletteDials(dials);
  let out = applyHueSpreadToPalette(hexes.slice(), d.spread);
  out = out.map((hex) => polishSingleHex(hex, d));
  return out;
}

/** Neighbor hues clustered around baseHue (low wheel contrast). */
function buildAnalogousHues(n, baseHue) {
  const h0 = wrapHue(baseHue);
  if (n <= 1) return [h0];
  const half = Math.min(40, 16 + (n - 2) * 3);
  return Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 0 : i / (n - 1);
    const offset = (t - 0.5) * 2 * half;
    return wrapHue(h0 + offset);
  });
}

/** Three anchors at 120°, repeated with slight fan for n > 3. */
function buildTriadicHues(n, baseHue) {
  const t0 = wrapHue(baseHue);
  const t1 = wrapHue(baseHue + 120);
  const t2 = wrapHue(baseHue + 240);
  const anchors = [t0, t1, t2];
  const hues = [];
  for (let i = 0; i < n; i++) {
    const ring = Math.floor(i / 3);
    const anchor = anchors[i % 3];
    hues.push(wrapHue(anchor + ring * 11));
  }
  return hues;
}

function huesForHarmonyMode(n, baseHue, harmonyMode) {
  switch (harmonyMode) {
    case "complementary":
      return buildComplementPairsHues(n, baseHue);
    case "triadic":
      return buildTriadicHues(n, baseHue);
    case "split":
      return buildSplitPopHues(n, baseHue);
    case "analogous":
    default:
      return buildAnalogousHues(n, baseHue);
  }
}

/** Lerp each hue toward an evenly spaced wheel as structure01 → 1. */
function blendHarmonyTowardWheel(n, baseHue, harmonyHues, structure01) {
  const t = Math.max(0, Math.min(1, structure01));
  if (t < 0.001) return harmonyHues;
  const wheel = buildContrastWheelHues(n, baseHue);
  return harmonyHues.map((h, i) => lerpHue(h, wheel[i], t));
}

/** Evenly spaced hues — strongest perceptual separation for n colors. */
function buildContrastWheelHues(n, baseHue) {
  if (n <= 1) return [wrapHue(baseHue)];
  const step = 360 / n;
  return Array.from({ length: n }, (_, i) => wrapHue(baseHue + i * step));
}

/** Alternate two poles on the wheel with small per-pair spread. */
function buildComplementPairsHues(n, baseHue) {
  const a = wrapHue(baseHue);
  const b = wrapHue(baseHue + 180);
  const bands = Math.ceil(n / 2);
  const spread = bands <= 1 ? 0 : Math.min(36, 72 / (bands - 1 || 1));
  const hues = [];
  for (let i = 0; i < n; i++) {
    const pole = i % 2;
    const band = Math.floor(i / 2);
    const h = pole === 0 ? a + band * spread : b + band * spread;
    hues.push(wrapHue(h));
  }
  return hues;
}

/** Split-complementary anchors, then fan outward for extra slots. */
function buildSplitPopHues(n, baseHue) {
  const h0 = wrapHue(baseHue);
  const h1 = wrapHue(baseHue + 150);
  const h2 = wrapHue(baseHue + 210);
  const anchors = [h0, h1, h2];
  const hues = [];
  for (let i = 0; i < n; i++) {
    const ring = Math.floor(i / 3);
    const anchor = anchors[i % 3];
    hues.push(wrapHue(anchor + ring * (14 + (i % 2) * 8)));
  }
  return hues;
}

function huesToHexes(hues, n, style, seedHex) {
  const spin = Math.random() * 360;
  const seedNorm =
    typeof seedHex === "string" ? normalizeHex(seedHex) : null;
  let seedS;
  let seedL;
  if (seedNorm) {
    const [, s, l] = hexToHsl(seedNorm);
    seedS = Number.isFinite(s) ? s : 55;
    seedL = Number.isFinite(l) ? l : 48;
    if (seedS < 12) seedS = 48;
  }

  let baseS;
  let baseL;
  let sMod;
  let lMod;

  if (style === "harmonic") {
    if (seedNorm) {
      baseS = Math.max(38, Math.min(94, seedS + (Math.random() - 0.5) * 14));
      baseL = Math.max(30, Math.min(70, seedL + (Math.random() - 0.5) * 12));
    } else {
      baseS = 58 + Math.random() * 22;
      baseL = 44 + Math.random() * 14;
    }
    sMod = (i) => (i % 3 === 0 ? 4 : i % 3 === 1 ? -3 : 0);
    lMod = (i) => (i % 4) * 3.5 - 5;
  } else if (style === "contrast") {
    if (seedNorm) {
      baseS = Math.max(55, Math.min(94, seedS + (Math.random() - 0.5) * 14));
      baseL = Math.max(34, Math.min(62, seedL + (Math.random() - 0.5) * 10));
    } else {
      baseS = 72 + Math.random() * 18;
      baseL = 42 + Math.random() * 10;
    }
    sMod = (i) => (i % 2 === 0 ? 6 : -4);
    lMod = (i) => (i % 3) * 6 - 6;
  } else if (style === "complements") {
    if (seedNorm) {
      baseS = Math.max(58, Math.min(94, seedS + (Math.random() - 0.5) * 12));
      baseL = Math.max(32, Math.min(58, seedL + (Math.random() - 0.5) * 12));
    } else {
      baseS = 78 + Math.random() * 16;
      baseL = 40 + Math.random() * 12;
    }
    sMod = (i) => (i % 2 === 0 ? 5 : -2);
    lMod = (i) => Math.floor(i / 2) * 4 - (n > 4 ? 4 : 0);
  } else {
    // split
    if (seedNorm) {
      baseS = Math.max(52, Math.min(94, seedS + (Math.random() - 0.5) * 14));
      baseL = Math.max(30, Math.min(58, seedL + (Math.random() - 0.5) * 12));
    } else {
      baseS = 70 + Math.random() * 22;
      baseL = 38 + Math.random() * 14;
    }
    sMod = (i) => ((i % 3) - 1) * 5;
    lMod = (i) => (i % 4) * 4 - 4;
  }

  const colors = [];
  for (let i = 0; i < n; i++) {
    const h = wrapHue(hues[i] ?? spin + i * 24);
    const s = Math.max(38, Math.min(94, baseS + sMod(i)));
    const l = Math.max(30, Math.min(70, baseL + lMod(i)));
    colors.push(hslToHex(h, s, l));
  }
  return colors;
}

function styleKeyForPaletteStructure(structure01) {
  const u = Math.max(0, Math.min(1, structure01)) * 3;
  if (u < 1) return "harmonic";
  if (u < 2) return "split";
  if (u < 3) return "complements";
  return "contrast";
}

/**
 * Palettes for recolor / programmatic modes. Random each call (new roll of the dice).
 * @param {number} count — number of distinct colors (capped at 64)
 * @param {Partial<PaletteDials>} [dials]
 */
export function generateHarmonicPalette(count, dials) {
  const dialNorm = normalizePaletteDials(dials);
  const n = Math.max(1, Math.min(64, count));

  const baseHue = dialNorm.baseColor
    ? hexToHsl(dialNorm.baseColor)[0]
    : Math.random() * 360;
  const harmonyHues = huesForHarmonyMode(
    n,
    baseHue,
    dialNorm.harmonyMode
  );
  const hues = blendHarmonyTowardWheel(
    n,
    baseHue,
    harmonyHues,
    dialNorm.paletteStructure
  );
  const style = styleKeyForPaletteStructure(dialNorm.paletteStructure);
  const raw = huesToHexes(hues, n, style, dialNorm.baseColor);
  // Hue fanning and the s/l clamps in polishing can land two slots on the same
  // color; every slot must stay distinct so recolors preserve color count.
  return ensureDistinctHexes(polishPalette(raw, dialNorm));
}

/**
 * Convert distinct non-default bead colors to grayscale.
 * Distinct colors (first-appearance = A, B, C…) are remapped to evenly spaced
 * grays from black to white for maximum contrast. Original brightness is
 * ignored. Empty / background cells stay unchanged.
 */
export function recolorPatternToGrayscale(pattern, defaultColor) {
  if (!Array.isArray(pattern) || pattern.length === 0) return pattern;

  const defaultCanon = normalizeHex(defaultColor) || defaultColor;

  const ordered = [];
  const seen = new Set();

  for (let i = 0; i < pattern.length; i++) {
    const raw = pattern[i];
    const canon = normalizeHex(raw);
    if (!canon) continue;
    if (canon === defaultCanon) continue;
    if (seen.has(canon)) continue;
    seen.add(canon);
    ordered.push(canon);
  }

  if (ordered.length === 0) return [...pattern];

  // Max contrast: spread evenly across full black → white (first appearance
  // order only decides which letter gets which step, not original brightness).
  const n = ordered.length;
  const grays = ensureDistinctHexes(
    ordered.map((_, i) => {
      const g = n === 1 ? 0 : Math.round((i / (n - 1)) * 255);
      return rgbToHex(g, g, g);
    })
  );

  const map = {};
  ordered.forEach((c, i) => {
    map[c] = grays[i];
  });

  return pattern.map((cell) => {
    const canon = normalizeHex(cell);
    if (!canon || canon === defaultCanon) return cell;
    return map[canon] ?? cell;
  });
}

/**
 * Keep one output color per input color so a 3-color pattern never recolors
 * down to 2. Duplicates are nudged to the nearest unused color (lightness
 * first, then hue) so they stay visually related to the color they came from.
 * `reservedKeys` are never returned (Empty / background must stay unassigned).
 */
export function ensureDistinctHexes(colors, reservedKeys) {
  if (!Array.isArray(colors)) return colors;

  const used = new Set();
  if (Array.isArray(reservedKeys)) {
    for (const key of reservedKeys) {
      if (key) used.add(key);
    }
  }
  const lightnessSteps = [];
  for (let d = 4; d <= 44; d += 4) lightnessSteps.push(d, -d);
  const hueSteps = [];
  for (let d = 10; d <= 180; d += 10) hueSteps.push(d, -d);

  return colors.map((color) => {
    const base = normalizeHex(color);
    if (!base) {
      // Not a hex we can shift; keep it and let it occupy its own slot.
      used.add(String(color));
      return color;
    }
    if (!used.has(base)) {
      used.add(base);
      return base;
    }

    const [h, s, l] = hexToHsl(base);

    for (const step of lightnessSteps) {
      const candidate = hslToHex(h, s, Math.max(6, Math.min(94, l + step)));
      if (!used.has(candidate)) {
        used.add(candidate);
        return candidate;
      }
    }

    for (const step of hueSteps) {
      const candidate = hslToHex(wrapHue(h + step), s, l);
      if (!used.has(candidate)) {
        used.add(candidate);
        return candidate;
      }
    }

    // Exhaustive fallback: walk the 24-bit space until something is free.
    let value = parseInt(base.slice(1), 16);
    for (let i = 0; i < 0xffffff; i++) {
      value = (value + 1) % 0x1000000;
      const candidate = `#${value.toString(16).padStart(6, "0")}`;
      if (!used.has(candidate)) {
        used.add(candidate);
        return candidate;
      }
    }
    return base;
  });
}

/** Expand #RGB to #rrggbb so short hex dedupes against full-length hex. */
function expandShortHex(input) {
  if (typeof input !== "string") return null;
  const m = /^#([a-fA-F0-9]{3})$/i.exec(input.trim());
  if (!m) return null;
  const [a, b, c] = m[1].split("");
  return `#${a}${a}${b}${b}${c}${c}`.toLowerCase();
}

/** One stable key per bead color: #rrggbb when parseable, else lowercased raw. */
export function beadColorKey(raw) {
  if (raw == null || raw === "") return null;
  const t = String(raw).trim();
  if (!t) return null;
  return normalizeHex(expandShortHex(t) || t) || t.toLowerCase();
}

/** Distinct non-default bead color keys in first-appearance order. */
export function orderedBeadColorKeys(pattern, defaultColor) {
  if (!Array.isArray(pattern)) return [];
  const defaultKey = beadColorKey(defaultColor);
  const ordered = [];
  const seen = new Set();
  for (const cell of pattern) {
    const key = beadColorKey(cell);
    if (key == null) continue;
    if (defaultKey != null && key === defaultKey) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    ordered.push(key);
  }
  return ordered;
}

/**
 * Colors shown in Pattern Preview: the full active palette first (so unused
 * slots stay visible and Flip can reach them), then any extra colors that
 * exist only on the grid.
 */
export function previewRoleColors(pattern, defaultColor, paletteColors) {
  const onGrid = orderedBeadColorKeys(pattern, defaultColor);
  const palette = paletteColorsForRecolor(paletteColors, defaultColor);
  if (palette.length === 0) return onGrid;
  const inPalette = new Set(palette);
  const extra = onGrid.filter((color) => !inPalette.has(color));
  return [...palette, ...extra];
}

/** Saved palette hexes as #rrggbb, deduped, original order kept. */
export function normalizePaletteColorList(colors) {
  if (!Array.isArray(colors)) return [];
  const out = [];
  const seen = new Set();
  for (const c of colors) {
    const n = normalizeHex(c) || normalizeHex(expandShortHex(String(c || "")));
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

/**
 * Palette hexes that can be assigned to beads. Empty / background is a
 * canvas slot, not a palette color, so it is never applied as a fill.
 */
export function paletteColorsForRecolor(colors, defaultColor) {
  const skip = beadColorKey(defaultColor);
  return normalizePaletteColorList(colors).filter(
    (color) => skip == null || color !== skip
  );
}

const DEFAULT_NEW_PAINT_COLOR = "#0066ff";
export const MAX_PREVIEW_ROLE_COLORS = 26;

/**
 * A paint color that is not already in `existing` and is not Empty.
 * Prefers the same default as PalettesPanel's Add Color.
 */
export function unusedPaintColor(existing, defaultColor) {
  const reserved = beadColorKey(defaultColor);
  const list = paletteColorsForRecolor(existing, defaultColor);
  const seed = DEFAULT_NEW_PAINT_COLOR;
  if (seed !== reserved && !list.includes(seed)) return seed;
  const assigned = ensureDistinctHexes(
    [...list, seed],
    reserved ? [reserved] : []
  );
  return assigned[assigned.length - 1];
}

/**
 * Position-based map from each distinct bead color to a saved palette color.
 * The palette repeats when a pattern uses more colors than the palette holds.
 * Empty / background is never assigned. Returns null when there is nothing
 * to recolor.
 */
export function buildPaletteRecolorMap(pattern, defaultColor, paletteColors) {
  const palette = paletteColorsForRecolor(paletteColors, defaultColor);
  if (palette.length === 0) return null;

  const ordered = orderedBeadColorKeys(pattern, defaultColor);
  if (ordered.length === 0) return null;

  const defaultKey = beadColorKey(defaultColor);
  // A short palette repeats to cover every role, then duplicates are separated
  // so each original color keeps its own distinct color. Empty is reserved so
  // a nudged duplicate can never become a blank bead.
  const assigned = ensureDistinctHexes(
    ordered.map((_, i) => palette[i % palette.length]),
    defaultKey ? [defaultKey] : []
  );

  const map = new Map();
  ordered.forEach((key, i) => {
    map.set(key, assigned[i]);
  });
  return map;
}

/**
 * Replace one bead color throughout a pattern. Empty/background cells stay
 * unchanged. Returns a copy when there is nothing to replace.
 */
export function replacePatternColor(pattern, defaultColor, fromColor, toColor) {
  if (!Array.isArray(pattern)) return pattern;
  const fromKey = beadColorKey(fromColor);
  const next = normalizeHex(toColor) || beadColorKey(toColor);
  const defaultKey = beadColorKey(defaultColor);
  if (!fromKey || !next) return [...pattern];
  if (defaultKey != null && fromKey === defaultKey) return [...pattern];
  if (fromKey === next) return [...pattern];

  const map = new Map([[fromKey, next]]);
  return applyRecolorMapToCells(pattern, defaultColor, map);
}

/** Apply a bead-color map to a list of cells, leaving empty/background cells alone. */
export function applyRecolorMapToCells(cells, defaultColor, map) {
  if (!Array.isArray(cells) || !map) return cells;
  const defaultKey = beadColorKey(defaultColor);
  return cells.map((cell) => {
    const key = beadColorKey(cell);
    if (key == null) return cell;
    if (defaultKey != null && key === defaultKey) return cell;
    return map.has(key) ? map.get(key) : cell;
  });
}

/** Recolor a pattern's distinct bead colors with a saved palette's colors. */
export function recolorPatternWithPaletteColors(
  pattern,
  defaultColor,
  paletteColors
) {
  const map = buildPaletteRecolorMap(pattern, defaultColor, paletteColors);
  if (!map) return Array.isArray(pattern) ? [...pattern] : pattern;
  return applyRecolorMapToCells(pattern, defaultColor, map);
}

/**
 * True when Flip color roles would change the grid: two or more bead colors,
 * or one bead color and a longer saved palette to rotate through.
 */
export function canFlipPatternColorRoles(pattern, defaultColor, paletteColors) {
  const roles = orderedBeadColorKeys(pattern, defaultColor);
  if (roles.length === 0) return false;
  const palette = paletteColorsForRecolor(paletteColors, defaultColor);
  return palette.length > roles.length || roles.length >= 2;
}

/**
 * Next arrangement of `colors` in lexicographic order. The last arrangement
 * wraps to the first, so repeated calls visit every permutation once.
 */
function nextColorPermutation(colors) {
  const next = [...colors];
  let pivot = next.length - 2;
  while (pivot >= 0 && next[pivot] >= next[pivot + 1]) pivot -= 1;
  if (pivot >= 0) {
    let successor = next.length - 1;
    while (next[successor] <= next[pivot]) successor -= 1;
    const swap = next[pivot];
    next[pivot] = next[successor];
    next[successor] = swap;
  }
  let left = pivot + 1;
  let right = next.length - 1;
  while (left < right) {
    const swap = next[left];
    next[left] = next[right];
    next[right] = swap;
    left += 1;
    right -= 1;
  }
  return next;
}

/**
 * Reassign which palette color plays which bead role.
 *
 * When a saved palette has more colors than the pattern uses, each flip
 * advances a sliding window so every palette color is used. Three colors
 * already on the grid cycle through all six arrangements. Any other count
 * reverses roles (first ↔ last).
 */
export function flipPatternColorRoles(pattern, defaultColor, paletteColors) {
  if (!Array.isArray(pattern) || pattern.length === 0) return pattern;

  const roles = orderedBeadColorKeys(pattern, defaultColor);
  if (roles.length === 0) return [...pattern];

  const palette = paletteColorsForRecolor(paletteColors, defaultColor);
  let assigned;

  if (palette.length > roles.length) {
    let nextOffset = 0;
    for (let start = 0; start < palette.length; start++) {
      const matches = roles.every(
        (role, i) => palette[(start + i) % palette.length] === role
      );
      if (matches) {
        nextOffset = (start + 1) % palette.length;
        break;
      }
    }
    assigned = roles.map(
      (_, i) => palette[(nextOffset + i) % palette.length]
    );
  } else if (roles.length === 3) {
    assigned = nextColorPermutation(roles);
  } else if (roles.length >= 2) {
    assigned = [...roles].reverse();
  } else {
    return [...pattern];
  }

  const map = new Map();
  roles.forEach((role, i) => {
    map.set(role, assigned[i]);
  });
  return applyRecolorMapToCells(pattern, defaultColor, map);
}

/**
 * Replace each distinct non-default bead color (same structure).
 * @param {Partial<PaletteDials>} [dials]
 */
export function recolorPatternWithHarmonicPalette(
  pattern,
  defaultColor,
  dials
) {
  if (!Array.isArray(pattern) || pattern.length === 0) return pattern;

  const defaultCanon = normalizeHex(defaultColor) || defaultColor;

  const ordered = [];
  const seen = new Set();

  for (let i = 0; i < pattern.length; i++) {
    const raw = pattern[i];
    const canon = normalizeHex(raw);
    if (!canon) continue;
    if (canon === defaultCanon) continue;
    if (seen.has(canon)) continue;
    seen.add(canon);
    ordered.push(canon);
  }

  if (ordered.length === 0) return [...pattern];

  const dn = normalizePaletteDials(dials);
  const paletteCore = generateHarmonicPalette(ordered.length, dn);
  const palette = ensureDistinctHexes(
    applyContrastRemapToPaletteOrder(ordered, paletteCore, dn.preserveContrast)
  );
  const map = {};
  ordered.forEach((c, idx) => {
    map[c] = palette[idx];
  });

  return pattern.map((cell) => {
    const canon = normalizeHex(cell);
    if (!canon || canon === defaultCanon) return cell;
    return map[canon] ?? cell;
  });
}
